/**
 * Hook for campaign games tab.
 * Resolves the selected campaign's games into full IGameSettings objects for the Games tab.
 *
 * Handles every campaign shape so one Games tab can serve all types:
 * - a list of game keys (prize pop / rebate / raw tournament) → resolved via the catalog,
 * - already-parsed games (parsed tournaments) → used as-is,
 * - a parsed-tournament `gamesKeys` map → resolved against the loaded sections.
 *
 * Virtualization is handled by VirtualizedGamesGrid downstream.
 */

import { useMemo } from 'react';

import { SECTION_NAMES } from '@repo/common';
import { useSelectedCampaign, type CampaignTypes } from 'features/campaigns';
import { resolveCampaignGames } from '../../../../../utils/resolveCampaignGames';
import { useAppStore } from 'store/app/appStore';
import type IGameSettings from 'types/models/IGameSettings';

export const useCampaignGamesTab = (campaignType: CampaignTypes) => {
  const campaign = useSelectedCampaign(campaignType);
  const supportedGames = useAppStore((state) => state.supportedGames);
  const topGames = useAppStore((state) => state.sections.find((s) => s.name === SECTION_NAMES.TOP)?.games);
  const allSectionGames = useAppStore((state) => state.sections.flatMap((s) => s.games));

  const allCampaignGames = useMemo(() => {
    if (!campaign || !campaign.games) {
      return [];
    }

    // IParsedTournament has games: IGameSettings[] (already resolved)
    if (
      'games' in campaign &&
      Array.isArray(campaign.games) &&
      campaign.games.length > 0 &&
      typeof campaign.games[0] !== 'string'
    ) {
      return campaign.games as IGameSettings[];
    }

    // ITournament has gamesKeys: { [key: string]: string } (object map)
    if ('gamesKeys' in campaign && campaign.gamesKeys) {
      return Object.keys(campaign.gamesKeys as Record<string, string>)
        .map((gameKey: string) => allSectionGames.find((game: IGameSettings) => game.gameKey === gameKey))
        .filter(Boolean) as IGameSettings[];
    }

    // Campaign's `games` property is a list of game keys.
    const topGameKeys = new Set(topGames?.map((g) => g.gameKey) ?? []);
    return resolveCampaignGames(campaign.games as string[], supportedGames, topGameKeys);
  }, [campaign, supportedGames, topGames, allSectionGames]);

  return {
    campaign,
    allCampaignGames,
  };
};
